import { PORTAL_RETRIEVAL_MESSAGES } from 'components/inboundReferrals/constants';

// The window.postMessage handshake with the Olio browser extension. The
// extension's content script listens on our own page's window: we post a job, it acks
// immediately (or it isn't installed), then posts the result whenever the retrieval
// finishes — minutes later for a recording a human drives.

const ACK_TIMEOUT_MS = 500;

export interface PortalRetrievalJob {
  // Replay: the saved script to execute. Absent for record-mode jobs.
  script?: { entryUrl: string; body: string; retrievalUrl?: string | null };
  // Where the extension opens the portal tab — the referral's live retrieval
  // pointer, for record AND replay. The script's stored entryUrl is record-time
  // (stale referral ids) and is only the extension's fallback when this is absent.
  entryUrl?: string;
  record?: boolean;
  params: Record<string, unknown>;
  context: Record<string, unknown>;
  expectedOutputs: string[];
  // Names in expectedOutputs that are documents: their marks record as `download`
  // statements, and their values come back as base64 file entries (see
  // services/portalDocuments for the upload hand-off).
  documentOutputs?: string[];
}

interface ExtensionMessage {
  type?: string;
  jobId?: string;
  probe?: boolean;
  ok?: boolean;
  values?: Record<string, unknown>;
  error?: string;
  status?: string;
}

// Live (non-terminal) states the conductor broadcasts while a job runs. waiting_login
// and confused halt the automation without ending the job — the result still arrives
// later on the result message.
export type PortalRetrievalStatus = 'running' | 'waiting_login' | 'confused';

export interface RunPortalRetrievalOptions {
  // Called on every status broadcast for the job. jobId is included so the caller can
  // restart a confused job (restartPortalRetrieval) while the run promise stays pending.
  onStatus?: (status: PortalRetrievalStatus, jobId: string) => void;
}

// Is the extension installed on this browser? It answers the probe instantly, so a
// short timeout is a reliable "not installed".
export function probePortalExtension(timeoutMs = 500): Promise<boolean> {
  return new Promise((resolve) => {
    const onMessage = (event: MessageEvent<ExtensionMessage>) => {
      if (event.source !== window) return;
      if (event.data?.type !== PORTAL_RETRIEVAL_MESSAGES.ack || !event.data.probe) return;
      clearTimeout(timer);
      window.removeEventListener('message', onMessage);
      resolve(true);
    };
    const timer = setTimeout(() => {
      window.removeEventListener('message', onMessage);
      resolve(false);
    }, timeoutMs);
    window.addEventListener('message', onMessage);
    window.postMessage({ type: PORTAL_RETRIEVAL_MESSAGES.probe }, window.location.origin);
  });
}

// Run one retrieval job. Rejects fast when the extension never acks; after the ack
// there is deliberately NO timeout — a record-mode job runs as long as the human takes.
// Status broadcasts (waiting_login, confused) surface through options.onStatus and never
// settle the promise — even a confused job keeps running until its result arrives.
export function runPortalRetrieval(
  job: PortalRetrievalJob,
  options?: RunPortalRetrievalOptions
): Promise<Record<string, unknown>> {
  return new Promise((resolve, reject) => {
    const jobId = crypto.randomUUID();

    const onMessage = (event: MessageEvent<ExtensionMessage>) => {
      if (event.source !== window) return;
      const data = event.data;
      if (!data || data.jobId !== jobId) return;

      if (data.type === PORTAL_RETRIEVAL_MESSAGES.ack) {
        clearTimeout(ackTimer);
        return;
      }
      if (data.type === PORTAL_RETRIEVAL_MESSAGES.status) {
        if (data.status) options?.onStatus?.(data.status as PortalRetrievalStatus, jobId);
        return;
      }
      if (data.type === PORTAL_RETRIEVAL_MESSAGES.result) {
        clearTimeout(ackTimer);
        window.removeEventListener('message', onMessage);
        if (data.ok) resolve(data.values ?? {});
        else reject(new Error(data.error || 'Portal retrieval failed.'));
      }
    };

    const ackTimer = setTimeout(() => {
      window.removeEventListener('message', onMessage);
      reject(new Error('Olio browser extension not detected.'));
    }, ACK_TIMEOUT_MS);

    window.addEventListener('message', onMessage);
    window.postMessage({ type: PORTAL_RETRIEVAL_MESSAGES.request, jobId, ...job }, window.location.origin);
  });
}

// Restart a confused job from scratch — the extension's conductor reruns the state
// machine in the same tab from the entry URL. The original run promise stays pending;
// the (re)run's outcome arrives on the same result message.
export function restartPortalRetrieval(jobId: string) {
  window.postMessage({ type: PORTAL_RETRIEVAL_MESSAGES.restart, jobId }, window.location.origin);
}
